// Fleet desk modules. Each face is a summary tile fed by the SAME TanStack
// query keys/options as the full panel behind its drawer, so opening the
// drawer is a cache hit. Faces are state-only: no buttons or links inside.
//
// Loading rule (plan Review Focus 2, see useBooks.ts): a query that is
// `isLoading` OR (`isPaused && data === undefined`) is still LOADING — never
// a resolved zero. While loading, faces render '—' and omit compare/footer so
// no `$0` / `0/25` / `NaN` can leak.
import { useCallback } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { auditApi, convictionApi, executionsApi, sectorShiftApi } from '../../api/client'
import { useWebSocketEvent, type WebSocketHookReturn } from '../../hooks/useWebSocket'
import type { Book, SectorExposure, SectorShiftConfig, SectorShiftProposal } from '../../types'
import { Donut } from '../viz/Donut'
import { MiniFunnel } from '../viz/MiniFunnel'
import { PipStrip } from '../viz/PipStrip'
import { BookMiniCard } from './BookMiniCard'
import { Module } from './Module'
import {
  DASH,
  num,
  RUNS_LIMIT,
  stillLoading,
  unavailable,
  type FleetModuleProps,
} from './fleetModuleHooks'
import { funnelStages, pendingProposals, runPips, sectorSlices, todayFills } from './fleetSummaries'

// ---------------------------------------------------------------- pipeline

export interface ResearchModuleProps {
  research: Book | undefined
  booksPending: boolean // useBooks().isLoading
}

/** Incubation funnel from the same `['conviction-status', id]` read as LifecycleFunnel. */
export function PipelineModule({ research, booksPending }: ResearchModuleProps) {
  const accountId = research?.id
  const q = useQuery({
    queryKey: ['conviction-status', accountId],
    queryFn: () => convictionApi.status(accountId),
    enabled: accountId !== undefined,
    retry: 1,
    refetchInterval: 30000,
  })

  // A disabled query is never "loading": an unresolved roster is loading
  // explicitly, a resolved roster without a research book is an error.
  const isLoading = booksPending || stillLoading(q)
  const isError = !isLoading && (research === undefined || unavailable(q))
  const byState = q.data?.candidates_by_state
  const settled = !isLoading && !isError && byState !== undefined
  const caption = settled ? `${num(byState.discovered) ?? 0} → ${num(byState.held) ?? 0}` : undefined

  return (
    <Module
      title="Incubation pipeline"
      caption={caption}
      drawerKey="pipeline"
      span={3}
      isLoading={isLoading}
      isError={isError}
      errorText={research === undefined ? 'books unavailable' : 'pipeline unavailable'}
    >
      <MiniFunnel stages={funnelStages(byState)} ariaLabel={`incubation pipeline ${caption ?? DASH}`} />
    </Module>
  )
}

// ------------------------------------------------------------------ sector

// SectorPanel's DEFAULT_MIN_FROM_WEIGHT (not exported): the shed bar used
// while `/sector-shift/config` is loading or has failed.
const DEFAULT_MIN_FROM_WEIGHT = 0.2
// Longest group name the donut centre sub (9 viewBox units) can carry.
const CENTER_NAME_MAX = 14
// Top groups the face legend shows before folding the rest into "Other":
// nine legend rows overflowed the 1280×720 fixed-tier cell (I7).
const FACE_MAX_SLICES = 5

/**
 * Sector mix of the research book. Same keys/options as SectorPanel so the
 * drawer is a cache hit; like the panel, error only when exposure AND
 * proposals both failed (a broker outage must not hide the proposal queue).
 */
export function SectorModule({ research, booksPending }: ResearchModuleProps) {
  const accountId = research?.id
  const exposureQ = useQuery<SectorExposure>({
    queryKey: ['sector-shift', 'exposure', accountId],
    queryFn: () => sectorShiftApi.exposure(accountId),
    enabled: accountId !== undefined,
    retry: 1,
    staleTime: 60000,
  })
  const configQ = useQuery<SectorShiftConfig>({
    queryKey: ['sector-shift', 'config', accountId],
    queryFn: () => sectorShiftApi.config(accountId),
    enabled: accountId !== undefined,
    retry: 1,
    staleTime: 300000,
  })
  const proposalsQ = useQuery<SectorShiftProposal[]>({
    queryKey: ['sector-shift', 'proposals', accountId],
    queryFn: () => sectorShiftApi.proposals(accountId),
    enabled: accountId !== undefined,
    retry: 1,
    refetchInterval: 30000,
  })

  const liveBar = Number(configQ.data?.min_from_weight)
  const minFromWeight = Number.isFinite(liveBar) && liveBar > 0 ? liveBar : DEFAULT_MIN_FROM_WEIGHT
  const isLoading = booksPending || stillLoading(exposureQ)
  const isError =
    !isLoading && (research === undefined || (unavailable(exposureQ) && unavailable(proposalsQ)))
  const { slices, dominant } = sectorSlices(exposureQ.data, minFromWeight, FACE_MAX_SLICES)
  const groupCount = exposureQ.data?.by_group?.length
  const dominantText = dominant ? `${dominant.label} ${dominant.value}%` : undefined
  // The 100-unit ring cannot fit "<group> <pct>" (I7: 150 units wide): the
  // centre is the percentage, the sub the group name cut to 14 characters.
  // The full name stays in the aria-label and the legend.
  const centerSub = dominant
    ? dominant.label.length > CENTER_NAME_MAX
      ? `${dominant.label.slice(0, CENTER_NAME_MAX)}…`
      : dominant.label
    : undefined
  // While config loads the bar is not yet known: the caption omits it (the
  // 0.20 fallback is shown only once the config read has failed).
  const shedBarText = stillLoading(configQ) ? '' : ` ${Math.round(minFromWeight * 100)}%`
  const pending = pendingProposals(proposalsQ.data)
  const exposureDown = unavailable(exposureQ)
  const proposalsUnknown = stillLoading(proposalsQ) || unavailable(proposalsQ)
  const groupsText =
    exposureDown || groupCount === undefined ? null : `${groupCount} group${groupCount === 1 ? '' : 's'}`
  const proposalsNode = proposalsUnknown ? (
    <span>proposals —</span>
  ) : pending > 0 ? (
    <span className="warn">
      {pending} shift proposal{pending === 1 ? '' : 's'} pending
    </span>
  ) : null

  return (
    <Module
      title="Sector mix"
      caption={`held vs shed bar${shedBarText}`}
      drawerKey="sector"
      span={3}
      isLoading={isLoading}
      isError={isError}
      errorText={research === undefined ? 'books unavailable' : 'sectors unavailable'}
    >
      {/* §6.4 gap: no per-sector target weight exists in any frontend type,
          so the thin outer (target) ring is omitted in phase 1. A failed
          exposure read shows its error text, never an empty ring. */}
      {exposureDown ? (
        <p className="module-state">sector exposure unavailable</p>
      ) : (
        <Donut
          slices={slices}
          centerLabel={dominant ? `${dominant.value}%` : DASH}
          centerSub={centerSub}
          legend
          legendExtra={(s) => (s.shed ? <span className="warn">shed</span> : null)}
          ariaLabel={`sector mix ${dominantText ?? DASH}`}
        />
      )}
      {/* Foot: total group count (the legend folds past the top five), then
          the proposals. Unknown proposals are '—', never an implied
          "0 pending". */}
      {(groupsText || proposalsNode) && (
        <p className="module-foot">
          {groupsText}
          {groupsText && proposalsNode ? ' · ' : null}
          {proposalsNode}
        </p>
      )}
    </Module>
  )
}

// ---------------------------------------------------------------- activity

/**
 * Today's fills and recent workflow runs. Same keys/options as
 * RecentExecutions and WorkflowRunsFeed (page 1). Cap rejects are NOT on the
 * face: CapRejectionsFeed is book-scoped and lives in the drawer. Fills are
 * broker-backed for the default book (spec §6.4: no per-book attribution).
 */
export function ActivityModule({ ws }: { ws: WebSocketHookReturn }) {
  const queryClient = useQueryClient()
  const fillsQ = useQuery({
    queryKey: ['recent-executions'],
    queryFn: () => executionsApi.getRecent(25),
    refetchInterval: 10000,
  })
  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['recent-executions'] })
  }, [queryClient])
  useWebSocketEvent(ws, 'position_update', invalidate)
  const runsQ = useQuery({
    queryKey: ['workflow-runs', 1],
    queryFn: () => auditApi.listRuns(RUNS_LIMIT, 1),
    refetchInterval: 10000,
  })

  const isLoading = stillLoading(fillsQ) || stillLoading(runsQ)
  const isError = !isLoading && unavailable(fillsQ) && unavailable(runsQ)
  const fills = fillsQ.data ? String(todayFills(fillsQ.data, new Date())) : DASH
  const runs = runsQ.data?.runs
  const ok = runs?.filter((r) => r.success).length ?? 0
  const runsText = runs ? `${ok} / ${runs.length - ok} fail` : DASH

  return (
    <Module
      title="Today"
      caption="default"
      drawerKey="activity"
      span={2}
      isLoading={isLoading}
      isError={isError}
      errorText="activity unavailable"
    >
      <p className="module-row">
        <span>fills</span>
        <b>{fills}</b>
      </p>
      <p className="module-row">
        <span>runs</span>
        <b>{runsText}</b>
      </p>
      {runs && <PipStrip pips={runPips(runs, RUNS_LIMIT)} ariaLabel="recent workflow runs" />}
    </Module>
  )
}

// ------------------------------------------------------------------- books

/**
 * Three per-book mini cards (research, validation, live). A role with no
 * registered book renders a "not yet created" card; per-book loading and
 * failure live inside the card, so the module itself only gates on the roster.
 */
export function BooksModule({ books, booksPending, booksError }: FleetModuleProps) {
  return (
    <Module
      title="Books"
      drawerKey="books"
      span={4}
      isLoading={booksPending}
      // A failed background roster refresh keeps its cached books (v5 keeps
      // data on status 'error'): error only when there is nothing to show.
      isError={booksError && books.length === 0}
      errorText="books unavailable"
    >
      <div className="books-row">
        {(['research', 'validation', 'live'] as const).map((role) => (
          <BookMiniCard key={role} book={books.find((b) => b.role === role)} role={role} />
        ))}
      </div>
    </Module>
  )
}
